import { Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../Utilities/AppContext";
import { GET } from "../../Utilities/BaseService";
import { S3Service } from "../../Utilities/S3Service";
import { awsDirStruct } from "../../Utilities/constants";

type Props = {
    name: string;
    details?: any;
};

const safeName = (v = "") =>
    v.replace(/[\/\\:*?"<>|]/g, "_").replace(/\s+/g, "_");

export const Header = ({ name, details }: Props) => {
    const [district, setDistrict] = useState<any>(null);
    const [village, setVillage] = useState<any>(null);
    const [hamlet, setHamlet] = useState<any>(null);
    const [powerstation, setPowerstation] = useState<any>(null);

    const fetchDistrict = async (id: string) => {
        if (!id) return setDistrict(null);
        const res = await GET("/region/district", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setDistrict(res?.data?.rows?.[0] || null);
    };

    const fetchVillage = async (id: string) => {
        if (!id) return setVillage(null);
        const res = await GET("/region/village", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setVillage(res?.data?.rows?.[0] || null);
    };

    const fetchHamlet = async (id: string) => {
        if (!id) return setHamlet(null);
        const res = await GET("/region/hamlet", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setHamlet(res?.data?.rows?.[0] || null);
    };

    const fetchPowerStation = async (id: string) => {
        if (!id) return setPowerstation(null);
        const res = await GET("/powerstation", {
            page: 1,
            limit: 10000,
            search: "",
            id,
        });
        setPowerstation(res?.data?.rows?.[0] || null);
    };

    useEffect(() => {
        fetchDistrict(details?.district);
        fetchVillage(details?.village);
        fetchHamlet(details?.hamlet);
        fetchPowerStation(details?.powerstation);
    }, [details]);

    const [settings, setSettings] = useState<any[]>([]);
    const { store, setStore } = useContext(AppContext);
    const [logoSrc, setLogoSrc] = useState("");

    const { data } = useQuery(["settings", logoSrc], () => GET("/settings"), {
        onSuccess(res) {
            if (res?.data?.settings) setSettings(res.data.settings);
        },
    });

    const getLogo = async () => {
        if (store.logoURL) {
            setLogoSrc(store.logoURL);
        } else {
            const response = await GET("/settings/logos", { default: true });
            const firstDefault = response.data.rows[0];
            const [{ url }] = await S3Service.getS3ByKey(
                `${awsDirStruct.logos}/${firstDefault.id}`
            );
            const blob = await (await fetch(url)).blob();
            const blobURL = URL.createObjectURL(blob);
            setLogoSrc(blobURL);
            setStore({ ...store, logoURL: blobURL });
        }
    };

    useEffect(() => {
        getLogo();
    }, []);

    return (
        <Grid
            container
            alignItems="flex-start"
            justifyContent="space-evenly"
            sx={{ px: 4, py: 2, position: "relative" }}
        >
            <Grid item flex={1} sx={{ textAlign: "center", padding: 0 }}>
                <img
                    style={{ aspectRatio: 1 }}
                    height="50px"
                    alt="logo"
                    src={logoSrc}
                />

                <Typography
                    variant="h6"
                    fontWeight="medium"
                    textTransform="uppercase"
                    letterSpacing={1}
                >
                    {settings?.find((s: any) => s.key === "name")?.value}
                </Typography>

                <Typography variant="body1" fontWeight="medium">
                    {settings?.find((s: any) => s.key === "address")?.value}
                </Typography>

                <Typography variant="body2" letterSpacing={1}>
                    {settings?.find((s: any) => s.key === "phone")?.value}
                </Typography>

                <Typography variant="body2" letterSpacing={1}>
                    {settings?.find((s: any) => s.key === "mobile")?.value}
                </Typography>

                {/* Display selected names safely */}
                <Typography variant="body2" fontWeight="medium" mt={2}>
                    {district?.name && `District: ${safeName(district.name)}`}{" "}
                    {powerstation?.name &&
                        `| Powerstation: ${safeName(powerstation.name)}`}{" "}
                    {village?.name && `| Village: ${safeName(village.name)}`}{" "}
                    {hamlet?.name && `| Hamlet: ${safeName(hamlet.name)}`}
                </Typography>

                <Typography
                    variant="body2"
                    fontWeight="medium"
                    mt={1}
                    textTransform="uppercase"
                    letterSpacing={1}
                    sx={{ whiteSpace: "pre" }}
                >
                    {name}
                </Typography>
            </Grid>
        </Grid>
    );
};
